import gql from 'graphql-tag';

const UPDATE_OFFERED_ITEMS = gql`
  mutation($offerings: [OfferedItemInput]) {
    updateOfferedItems(items: $offerings) {
      itemId
      serviceItem {
        itemId
        name
        invoiceDescription
        quantityType
        roundUpValue
        unitPrice
        category {
          categoryId
          name
        }
      }
      serviceCatalogItem {
        itemId
        name
      }
      status
      unitPrice
      qty
      amount
      discountRate
      notes
      revisedQuantity
      revisedAmount
      revisedUnitPrice
      revisedDiscountRate
      adjustedBlockMoney
      adjustedBlockHour
      eligibleBlockMoney
      eligibleBlockHour
      afterHours
      operationStatus

      worklog {
        ticket {
          displayId
          ticketId
        }
      }
    }
  }
`;

const PUSH_INVOICE = gql`
  mutation($clientId: ID!, $invoiceId: ID!, $integrationType: String) {
    pushInvoiceToThirdPartyApp(
      invoiceId: $invoiceId
      clientId: $clientId
      integrationType: $integrationType
    ) {
      invoiceId
      displayId
      client {
        accountId
        name
      }
      status
    }
  }
`;

const SEND_INVOICE = gql`
  mutation($invoiceId: ID!) {
    sendInvoice(invoiceId: $invoiceId)
  }
`;

const SEND_INVOICES = gql`
  mutation($input: [InvoiceIdentifierInput!]!) {
    sendInvoices(input: $input) {
      invoiceId
      emailOperationalStatus
    }
  }
`;

const PAY_INVOICE = gql`
  mutation payInvoice($invoicePayment: InvoicePaymentInput!) {
    payInvoice(input: $invoicePayment)
  }
`;

const UPDATE_INVOICE = gql`
  mutation updateInvoice(
    $updatedInvoice: UpdateInvoiceInput!
    $isTaxSplitEnabled: Boolean!
  ) {
    updateInvoice(input: $updatedInvoice) {
      partiallyPaid
      invoiceId
      displayId
      amountPaid
      amountDue
      transactions {
        transactionId
        mappings {
          mappingId
          paymentSyncStatus
          errorDetails
        }
      }
      client {
        accountId
        name
        logoFileName
      }
      billTo {
        id
        name
        line1
        line2
        line3
        countryCode
        stateCode
        city
        postalCode
      }
      shipTo {
        id
        name
        line1
        line2
        line3
        countryCode
        stateCode
        city
        postalCode
      }
      senderAddress
      billingAddress
      # shippingAddress
      title
      footer
      memo
      invoiceDate
      dueDate
      customFields
      discountAmount
      taxes {
        tax {
          taxId
          name
          rates {
            rateId
            name
            rateValue
          }
        }
        rate @include(if: $isTaxSplitEnabled) {
          rateId
          name
          rateValue
        }
        taxRate
        taxableAmount
        taxAmount
      }
      conversationIds
      additionalDiscountRate
      additionalDiscount
      additionalDiscountType
      totalAmount
      statusEnum
      operationStatus
      sentToClient
      sendEmailError
      emailOperationalStatus
      pushedToIntegratedAppStatus
      pushedToIntegratedAppError
      pushPaymentToIntegratedAppStatus
      pushPaymentToIntegratedAppError
      items {
        itemId
        discountAmount
        itemOrder
        serviceItem {
          itemId
          name
          invoiceDescription
          quantityType
          roundUpValue
          unitPrice
          category {
            categoryId
            name
          }
          businessHoursUnitPrice
          afterHoursUnitPrice
          useAsWorklogItem
          quantity
        }
        serviceCatalogItem {
          itemId
          name
          itemType
          serviceTypeItem {
            itemId
            offeringType
          }
        }
        discountRate
        discountType
        billedDate
        details
        quantity
        unitPrice
        amount
        taxable
        tax {
          taxId
          name
          rates {
            rateId
            name
            rateValue
          }
        }
        offeredItem {
          type
          workItem {
            workId
            module
          }
          itemId
          site {
            id
            name
          }
        }
      }
      notes
      invoicePaymentTerm {
        paymentTermId
        paymentTermName
        paymentTermValue
        placeholder
        systemPaymentTerm
      }
      paymentMethod {
        paymentMethodId
        paymentMethodName
      }
      paymentDate
      paymentReference
      paymentLinkToken
      paymentLink
    }
  }
`;

const CREATE_CLIENT_INVOICE = gql`
  mutation createInvoice($createInvoice: CreateInvoiceInput!) {
    createInvoice(input: $createInvoice) {
      invoiceId
      displayId
    }
  }
`;

const CREATE_INVOICE_FROM_WORK = gql`
  mutation createInvoiceFromWork($input: CreateInvoiceFromWorkInput!) {
    createInvoiceFromWork(input: $input) {
      invoiceId
      displayId
    }
  }
`;

const DELETE_INVOICE = gql`
  mutation($invoiceId: ID!) {
    deleteInvoice(invoiceId: $invoiceId)
  }
`;

const UPDATE_INVOICE_VIEW_STATUS = gql`
  mutation {
    updateInvoiceViewedByClientStatus
  }
`;

const UPDATE_QUOTE_VIEW_STATUS = gql`
  mutation {
    updateQuoteViewedByClientStatus
  }
`;

const DELETE_INVOICES = gql`
  mutation($input: [InvoiceIdentifierInput!]!) {
    deleteInvoices(input: $input)
  }
`;

const UPDATE_INVOICE_PAYMENT_TERMS = gql`
  mutation updatePaymentTerms($input: UpdatePaymentTermInput!) {
    updatePaymentTerms(input: $input) {
      paymentTermId
      paymentTermName
      paymentTermValue
    }
  }
`;

const UPDATE_INVOICE_PAYMENT_METHODS = gql`
  mutation updatePaymentMethods($input: UpdatePaymentMethodsInput!) {
    updatePaymentMethods(input: $input) {
      paymentMethodId
      paymentMethodName
    }
  }
`;

const BULK_EXPORT_INVOICES = gql`
  mutation($input: SelectionInput) {
    exportInvoicesAsCSV(input: $input)
  }
`;

const APPROVE_OFFERED_ITEMS = gql`
  mutation($input: SelectionInput!) {
    approveOfferedItems(input: $input) {
      itemId
      serviceItem {
        itemId
        name
        invoiceDescription
        quantityType
        roundUpValue
        unitPrice
        category {
          categoryId
          name
        }
      }
      serviceCatalogItem {
        itemId
        name
      }
      status
      unitPrice
      qty
      amount
      discountRate
      notes
      revisedQuantity
      revisedAmount
      revisedUnitPrice
      revisedDiscountRate
      adjustedBlockMoney
      adjustedBlockHour
      eligibleBlockMoney
      eligibleBlockHour
      afterHours
      operationStatus

      worklog {
        ticket {
          displayId
          ticketId
        }
      }
    }
  }
`;

const IGNORE_OFFERED_ITEMS = gql`
  mutation($input: SelectionInput!) {
    ignoreOfferedItems(input: $input) {
      itemId
      serviceItem {
        itemId
        name
        invoiceDescription
        quantityType
        roundUpValue
        unitPrice
        category {
          categoryId
          name
        }
      }
      status
      unitPrice
      qty
      amount
      discountRate
      notes
      revisedQuantity
      revisedAmount
      revisedUnitPrice
      revisedDiscountRate
      adjustedBlockMoney
      adjustedBlockHour
      eligibleBlockMoney
      eligibleBlockHour
      afterHours
      operationStatus

      worklog {
        ticket {
          displayId
          ticketId
        }
      }
    }
  }
`;

const BULK_MARK_INVOICES_AS_PAID = gql`
  mutation($input: MarkInvoicesAsPaidInput) {
    markInvoicesAsPaid(input: $input)
  }
`;

const GENERATE_PAYMENT_LINK_TOKEN = gql`
  mutation($input: InvoiceIdentifierInput!) {
    generatePaymentLinkToken(input: $input)
  }
`;

const GENERATE_PAYMENT_LINK = gql`
  mutation($input: InvoiceIdentifierInput!) {
    generatePaymentLink(input: $input)
  }
`;

const GET_INVOICE_VIEWS_COUNT = gql`
  query getInvoiceViewsCount($viewIds: [ID]) {
    getInvoiceViewsCount(viewIds: $viewIds) {
      viewId
      count
    }
  }
`;

export {
  GET_INVOICE_VIEWS_COUNT,
  UPDATE_OFFERED_ITEMS,
  UPDATE_INVOICE,
  PUSH_INVOICE,
  SEND_INVOICE,
  SEND_INVOICES,
  PAY_INVOICE,
  DELETE_INVOICE,
  CREATE_CLIENT_INVOICE,
  CREATE_INVOICE_FROM_WORK,
  UPDATE_INVOICE_VIEW_STATUS,
  UPDATE_QUOTE_VIEW_STATUS,
  DELETE_INVOICES,
  UPDATE_INVOICE_PAYMENT_TERMS,
  UPDATE_INVOICE_PAYMENT_METHODS,
  BULK_EXPORT_INVOICES,
  APPROVE_OFFERED_ITEMS,
  IGNORE_OFFERED_ITEMS,
  BULK_MARK_INVOICES_AS_PAID,
  GENERATE_PAYMENT_LINK,
  GENERATE_PAYMENT_LINK_TOKEN
};
